import { useState, useEffect, useRef, useContext } from 'react'
import { get, supported } from '@github/webauthn-json'
import Link from '@talkdesk/cobalt-react-link'
import Flex from '@talkdesk/cobalt-react-flex'
import { Text } from '@talkdesk/cobalt-react-typography'
import MFAHeader from '../MFAHeader'
import ErrorMessage from '../ErrorMessage'
import WebAuthnLoginAnimation from './WebAuthnLoginAnimation'
import { WEB_AUTH as WebAuthnType } from '../../constants/mfa.types'
import useMFA from '../../hooks/useMFA'
import { LoginContext } from '../../contexts/Login'
import {
  WEB_AUTHN_TITLE,
  WEB_AUTHN_DESCRIPTION,
  WEB_AUTHN_NOT_SUPPORTED_ERROR_PART1,
  WEB_AUTHN_NOT_SUPPORTED_ERROR_PART2,
  INVALID_WEB_AUTHN_ERROR_MESSAGE,
  HELP,
  MFA_RETRY_ACTION
} from '../../constants/ui.labels'
import { LEARN_MORE_ABOUT_SECURITY_KEYS } from '../../constants/links'
import '../../../assets/css/security-key-box.css'

export const WebAuthn = ({
  talkdeskIdAssetsURL,
  csrf,
  ctx,
  redirect,
  data,
  webAuthnTokenTtl,
  loginTimestamp,
  webAuthnTokenTtlHeadroomSeconds = 5,
  brandingSettings
}) => {
  const mountedRef = useRef(true)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorType, setErrorType] = useState(null)

  const { doLogin, errorMessage, showError: showMfaError } = useMFA({
    redirect,
    invalidCodeErrorMessage: () => INVALID_WEB_AUTHN_ERROR_MESSAGE,
    genericErrorMessage: () => INVALID_WEB_AUTHN_ERROR_MESSAGE
  })

  const currentTimeSeconds = () => new Date().getTime() / 1000

  const calculateRemainingTtl = () =>
    loginTimestamp +
    webAuthnTokenTtl -
    webAuthnTokenTtlHeadroomSeconds -
    currentTimeSeconds()

  const doWhenMounted = fn => result => {
    if (mountedRef.current) return fn(result)
  }

  const handleSignSuccess = result => {
    setShowError(false)
    setErrorType(null)
    setShowSuccess(true)
    const mfa = {
      type: WebAuthnType,
      payload: {
        requestId: data.requestId,
        credential: result
      }
    }
    doLogin(csrf, ctx.credentials, mfa)
  }

  const handleSignError = () => {
    setErrorType('sign_error')
    setShowError(true)
  }

  const sign = () => {
    const publicKeyCredential =
      data.assertionRequest.publicKeyCredentialRequestOptions
    publicKeyCredential.timeout = calculateRemainingTtl()
    get({ publicKey: publicKeyCredential }).then(
      doWhenMounted(handleSignSuccess),
      doWhenMounted(handleSignError)
    )
  }

  const ensureSupported = () =>
    new Promise((resolve, reject) => {
      if (supported()) {
        resolve()
      } else {
        reject()
      }
    }).catch(() => {
      setErrorType('not_supported')
      setShowError(true)
    })

  const signIfSupported = () => ensureSupported().then(sign)

  const retry = () => {
    setShowError(false)
    setErrorType(null)
    setTimeout(signIfSupported, 500)
  }

  useEffect(() => {
    mountedRef.current = true
    signIfSupported()
    return () => {
      mountedRef.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const renderErrorMessage = () => {
    if (errorType === 'not_supported' || errorType === 'sign_error') {
      return (
        <Text inline>
          {WEB_AUTHN_NOT_SUPPORTED_ERROR_PART1}{' '}
          <Link onClick={retry}>{MFA_RETRY_ACTION}</Link>
          {'. ' + WEB_AUTHN_NOT_SUPPORTED_ERROR_PART2 + ' '}
          <Link
            target="_blank"
            href={LEARN_MORE_ABOUT_SECURITY_KEYS(brandingSettings)}
          >
            {HELP}
          </Link>
        </Text>
      )
    }
    return errorMessage || null
  }

  return (
    <>
      <MFAHeader
        icon="security_key"
        iconColor="primary600"
        title={WEB_AUTHN_TITLE}
        subtitle={WEB_AUTHN_DESCRIPTION}
      />
      {(showError || showMfaError) && (errorType || errorMessage) ? (
        <ErrorMessage>{renderErrorMessage()}</ErrorMessage>
      ) : null}
      <div className="security-key">
        <Flex
          className="security-key-box"
          alignX="center"
          alignY="center"
          height="100%"
          padding={4}
          style={{
            border: '1px solid var(--gray-300)',
            borderRadius: '4px',
            marginBottom: '16px'
          }}
        >
          <WebAuthnLoginAnimation
            talkdeskIdAssetsURL={talkdeskIdAssetsURL}
            isValid={showSuccess}
          />
        </Flex>
      </div>
    </>
  )
}

const WebAuthWithContext = props => {
  const ctx = useContext(LoginContext)
  return <WebAuthn ctx={ctx} {...props} />
}
WebAuthWithContext.displayName = 'WEB_AUTHN'

export default WebAuthWithContext
